import { createContext, useContext, useMemo, useCallback, memo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useCosmosQueryClient } from 'providers/clients/CosmosQueryClientProvider';
import { useDataProvider } from 'react-admin';
import ReplayIcon from '@mui/icons-material/Replay';
import {
  ApiErrorResponse,
  BloxCustomDataProviderType,
  EditorialConfigData,
  GetEditorialConfigResult,
} from '../../utility/types/dataProvider';
import ErrorAlerts, { ErrorDetails } from '../../components/generic/ErrorAlerts';
import SkeletonBox from '../../components/generic/SkeletonBox';
import { GetWorkflowsResult, Workflow } from './extensions/Workflows';
import { useTranslation } from 'react-i18next';

export type CosmosEditorialContextType = {
  editorialConfig: EditorialConfigData | undefined;
  siteWorkflows: Workflow[] | undefined;
  siteHasWorkflows: boolean;
  dashboard?: string;
};

export const CosmosEditorialContext = createContext<
  CosmosEditorialContextType | undefined
>(undefined);

export const useCosmosEditorial = () => {
  const context = useContext(CosmosEditorialContext);
  if (!context) {
    throw new Error(
      'useCosmosEditorial must be used within a CosmosEditorialContextProvider',
    );
  }
  return context;
};

type CosmosEditorialContextProviderProps = {
  children?: React.ReactNode;
  dashboard?: string;
};

export const CosmosEditorialContextProvider = memo(
  ({ children, dashboard }: CosmosEditorialContextProviderProps) => {
    const { t } = useTranslation();
    const { queryClient } = useCosmosQueryClient();
    const dataProvider = useDataProvider<BloxCustomDataProviderType>();

    // Translation strings
    const strRetry = t('blox.actions.retry');
    const strLoadEditorial = t('blox.editorial.loading');

    const {
      data: editorialCfg,
      status: editorialCfgStatus,
      error: editorialCfgErr,
    } = useQuery<GetEditorialConfigResult, ApiErrorResponse, EditorialConfigData>({
      queryKey: ['getEditorialConfig'],
      queryFn: async () => {
        const result = await dataProvider.getEditorialConfig();
        return result;
      },
      select: (data) => data?.data,
      retry: false,
      refetchInterval: false,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    });

    const {
      data: siteWorkflows,
      status: siteWorkflowsStatus,
      error: siteWorkflowsError,
    } = useQuery<GetWorkflowsResult, ApiErrorResponse, GetWorkflowsResult['data']>({
      queryKey: ['getWorkflows'],
      queryFn: async () => {
        const result = await dataProvider.getWorkflows();
        return result;
      },
      select: (data) => data?.data,
      retry: false,
      refetchInterval: false,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    });

    // Are there any workflows on this site?
    const workflowsErrored = siteWorkflowsStatus === 'error';

    const resolvedWorkflows = useMemo(() => {
      return workflowsErrored ? [] : siteWorkflows;
    }, [workflowsErrored, siteWorkflows]);

    const siteHasWorkflows = useMemo(() => {
      return Array.isArray(resolvedWorkflows) && Boolean(resolvedWorkflows.length);
    }, [resolvedWorkflows]);

    // Allow retrying from error ui
    // invalidate all queries within this context
    const handleRetryQueries = useCallback(async () => {
      await queryClient.invalidateQueries({ queryKey: ['getEditorialConfig'] });
      await queryClient.invalidateQueries({ queryKey: ['getWorkflows'] });
    }, [queryClient]);

    // Editorial context value
    const contextValue = useMemo(() => {
      return {
        dashboard,
        editorialConfig: editorialCfg,
        siteWorkflows: resolvedWorkflows,
        siteHasWorkflows,
      };
    }, [dashboard, editorialCfg, resolvedWorkflows, siteHasWorkflows]);

    /**
     * Ensure queries complete before rendering
     */
    const anyErrors = editorialCfgStatus === 'error';
    const anyLoading =
      editorialCfgStatus === 'pending' || siteWorkflowsStatus === 'pending';

    if (anyLoading) {
      return (
        <SkeletonBox
          text={strLoadEditorial}
          width="100%"
          height="100%"
        />
      );
    }

    /**
     * Render error if any errors occurred
     */
    if (anyErrors) {
      const errorList: ErrorDetails[] = [];

      if (editorialCfgErr) {
        errorList.push({
          status: `${editorialCfgErr?.status || editorialCfgErr?.axiosStatus}`,
          message: `${editorialCfgErr?.message || editorialCfgErr?.axiosMessage}`,
          fullDetails: editorialCfgErr,
        });
      }

      if (siteWorkflowsError) {
        errorList.push({
          status: `${siteWorkflowsError?.status || siteWorkflowsError?.axiosStatus}`,
          message: `${siteWorkflowsError?.message || siteWorkflowsError?.axiosMessage}`,
          fullDetails: siteWorkflowsError,
        });
      }

      return (
        <ErrorAlerts
          severity="warning"
          buttonText={strRetry}
          buttonIcon={<ReplayIcon />}
          buttonOnClick={handleRetryQueries}
          showDetails={true}
          errors={errorList}
        />
      );
    }

    return (
      <CosmosEditorialContext.Provider value={contextValue}>
        {children}
      </CosmosEditorialContext.Provider>
    );
  },
);
CosmosEditorialContextProvider.displayName = 'CosmosEditorialContextProvider';
